/** SUNTRONIX ONE — Master Customers directory (Level 1).
 *
 *  Shows ONLY master customers (top of the hierarchy).  Subsidiaries live
 *  inside the Master Customer 360 (`/customers/:masterClientId`).
 *
 *  Fixes the previous behaviour where CustomerMatrix flattened master
 *  clients + subsidiaries onto one screen.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { OrgName, StateCountry, sameText } from "@/components/identity/Identity";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search, ArrowRight, Building2, Globe, Eye } from "lucide-react";
import { usePreview } from "@/context/PreviewContext";

export default function MasterCustomersList() {
  const navigate = useNavigate();
  const { start: startPreview } = usePreview();
  const [items, setItems] = useState([]);
  const [rollups, setRollups] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master-clients");
      const list = data.items || [];
      setItems(list);
      // Fire dashboards in parallel — best-effort counts
      const dash = await Promise.all(list.map((m) =>
        api.get(`/master-clients/${m.id}/dashboard`)
          .then((r) => [m.id, r.data]).catch(() => [m.id, null])));
      const map = {};
      dash.forEach(([id, d]) => { if (d) map[id] = d; });
      setRollups(map);
    } catch (e) {
      toast.error("Could not load master customers");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const needle = q.toLowerCase();
    return items.filter((m) =>
      (m.name || "").toLowerCase().includes(needle)
      || (m.code || "").toLowerCase().includes(needle)
      || (m.country || "").toLowerCase().includes(needle));
  }, [items, q]);

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="master-customers-list">
      <PageHeader
        eyebrow="Level 1 · Directory"
        title="Customers"
        context="Master customers and their organisational structure."
        actions={
          <Button variant="create" onClick={() => navigate("/customer/new")}
            data-testid="master-customers-add">
            <Plus className="h-4 w-4 mr-1" strokeWidth={2} />
            Add Master Customer
          </Button>
        }
        testId="master-customers-header"
      />

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.6} />
        <Input className="pl-8"
          placeholder="Search master customer name, code, country…"
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="master-customers-search" />
      </div>

      <Section title={`${filtered.length} Master Customer${filtered.length === 1 ? "" : "s"}`}
        hint="Click any row to open the Master Customer 360 — subsidiaries live inside."
        testId="master-customers-section">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[color:var(--st-surface-alt)] text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">
              <tr>
                <th className="text-left px-3 py-2">Master Customer</th>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">HQ Country</th>
                <th className="text-left px-3 py-2">Currency</th>
                <th className="text-right px-3 py-2">Subsidiaries</th>
                <th className="text-right px-3 py-2">Assets</th>
                <th className="text-right px-3 py-2">Employees</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="p-6 text-center text-slate-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="p-6 text-center text-slate-400">
                  No master customers match.
                </td></tr>
              )}
              {filtered.map((m) => {
                const d = rollups[m.id] || {};
                return (
                  <tr key={m.id}
                    className="border-t border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)] cursor-pointer"
                    onClick={() => navigate(`/customers/${m.id}`)}
                    data-testid={`master-row-${m.code || m.id}`}>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded bg-slate-900 text-white grid place-items-center font-heading text-[11px]">
                          {(m.code || m.name || "M").slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-slate-900"><OrgName name={m.name} testid={`master-name-${m.code || m.id}`} /></div>
                          {m.legal_name && !sameText(m.legal_name, m.name) && (
                            <div className="text-[11px] text-slate-500"><OrgName name={m.legal_name} /></div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-[11px]">{m.code || "—"}</td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1">
                        <Globe className="h-3 w-3 text-slate-400" strokeWidth={1.6} />
                        {(m.country || m.country_of_incorporation) ? <StateCountry country={m.country || m.country_of_incorporation} /> : "—"}
                      </span>
                    </td>
                    <td className="px-3 py-3">{m.base_currency || m.currency || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono">{d.customers ?? "—"}</td>
                    <td className="px-3 py-3 text-right font-mono">{d.assets ?? "—"}</td>
                    <td className="px-3 py-3 text-right font-mono">{d.employees ?? "—"}</td>
                    <td className="px-3 py-3">
                      <span className={"px-2 py-0.5 text-[11px] rounded border " +
                        (m.active !== false
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-600 border-slate-200")}>
                        {m.active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => { e.stopPropagation(); startPreview(m.id); }}
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 mr-1 rounded border border-yellow-500 bg-yellow-50 hover:bg-yellow-100 text-yellow-900"
                        data-testid={`master-preview-${m.code || m.id}`}>
                        <Eye className="h-3 w-3" strokeWidth={2} /> Preview
                      </button>
                    </td>
                    <td className="px-3 py-3 text-slate-400"><ArrowRight className="h-4 w-4" strokeWidth={1.6} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
